import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "@app/store";
import { getAllSubnetsRequest } from "./subnetsApiService";
import { ISubnet, Vnet } from "../types";

const subnetsAdapter = createEntityAdapter<ISubnet>({
  selectId: (item) => item.subnet_name,
});

const initialState = subnetsAdapter.getInitialState({
  status: "",
  selectedVnet: "",
});

const subnetsSlice = createSlice({
  name: "subnets",
  initialState,
  reducers: {
    selectVnet(state, action: PayloadAction<string>) {
      state.selectedVnet = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubnets.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchSubnets.fulfilled,
        (state, { payload }: PayloadAction<ISubnet[]>) => {
          subnetsAdapter.setAll(state, payload);
          state.status = "";
        },
      )
      .addCase(fetchSubnets.rejected, (state) => {
        state.status = "";
      });
  },
});

export const fetchSubnets = createAsyncThunk(
  "subnets/fetchSubnets",
  async (customerId: string) => {
    const res = await getAllSubnetsRequest(customerId);
    return res.data;
  },
);

export const { selectVnet } = subnetsSlice.actions;
export default subnetsSlice.reducer;

const subnetSelector = subnetsAdapter.getSelectors(
  (state: RootState) => state.subnets,
);

export const getAllSubnets = (state: RootState) =>
  subnetSelector.selectAll(state);

export const selectSubnetsByVnet = (state: RootState) => {
  const selectedVnet = state.subnets.selectedVnet;
  return selectedVnet
    ? subnetSelector
        .selectAll(state)
        .filter((subnet) => subnet.vnet_name === selectedVnet)
    : [];
};

export const selectVnets = (state: RootState) => {
  return subnetSelector.selectAll(state).reduce((uniqueVnets, subnet) => {
    const exists = uniqueVnets.some(
      (vnet) =>
        vnet.vnet_name === subnet.vnet_name &&
        vnet.cloud_platform === subnet.cloud_platform,
    );
    if (!exists) {
      uniqueVnets.push({
        vnet_name: subnet.vnet_name,
        cloud_platform: subnet.cloud_platform,
      });
    }
    return uniqueVnets;
  }, [] as Vnet[]);
};

export const selectSubnetStatus = (state: RootState) => state.subnets.status;

export const selectSubnetByName = (
  state: RootState,
  subnetName: string | undefined,
) => {
  return subnetName ? subnetSelector.selectById(state, subnetName) : undefined;
};
